import { useCallback, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import { showSuccessToast } from "app/actions/toast_actions";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { useDeleteKeywordGroup } from "app/services/keyword-groups";

import type { IKeywordsGroupEditorController } from "./useKeywordsGroupEditorController";
import { useKeywordsGroupEditorController } from "./useKeywordsGroupEditorController";

interface IUseKeywordGroupModalWorkflowConfig {
    onDeleteSuccess?: (deletedGroup: IUserKeywordGroup) => void;
    onEditorOpened?: () => void;
}

export interface IDeleteConfirmationModalProps {
    isOpen: boolean;
    listName: string;
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

export interface IKeywordGroupModalWorkflow {
    controller: IKeywordsGroupEditorController;
    handleBeforeDelete: (group: IUserKeywordGroup) => void;
    requestDelete: (group: IUserKeywordGroup) => void;
    deleteConfirmationModalProps: IDeleteConfirmationModalProps;
}

export const useKeywordGroupModalWorkflow = (
    config: IUseKeywordGroupModalWorkflowConfig = {},
): IKeywordGroupModalWorkflow => {
    const { onDeleteSuccess, onEditorOpened } = config;
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const controller = useKeywordsGroupEditorController();
    const [groupToDelete, setGroupToDelete] = useState<IUserKeywordGroup | undefined>();
    const [shouldRestoreEditor, setShouldRestoreEditor] = useState(false);
    const { mutate: deleteKeywordGroup, isPending: isDeletePending } = useDeleteKeywordGroup();

    const handleBeforeDelete = useCallback(
        (group: IUserKeywordGroup) => {
            controller.hide();
            setGroupToDelete(group);
            setShouldRestoreEditor(true);
        },
        [controller],
    );

    const requestDelete = useCallback((group: IUserKeywordGroup) => {
        setGroupToDelete(group);
        setShouldRestoreEditor(false);
    }, []);

    const handleDeleteCancel = useCallback(() => {
        if (isDeletePending) {
            return;
        }

        setGroupToDelete(undefined);

        if (shouldRestoreEditor) {
            controller.show();
            setShouldRestoreEditor(false);
            onEditorOpened?.();
        }
    }, [isDeletePending, shouldRestoreEditor, controller, onEditorOpened]);

    const handleDeleteConfirm = useCallback(() => {
        if (!groupToDelete) {
            return;
        }

        deleteKeywordGroup(
            { group: groupToDelete },
            {
                onSuccess: () => {
                    dispatch(
                        showSuccessToast(
                            translate("delete.list.wizard.toast", { listName: groupToDelete.Name }),
                        ),
                    );
                    onDeleteSuccess?.(groupToDelete);
                    setGroupToDelete(undefined);
                    setShouldRestoreEditor(false);
                },
            },
        );
    }, [groupToDelete, deleteKeywordGroup, dispatch, translate, onDeleteSuccess]);

    const deleteConfirmationModalProps: IDeleteConfirmationModalProps = useMemo(
        () => ({
            isOpen: Boolean(groupToDelete),
            listName: groupToDelete?.Name ?? "",
            isLoading: isDeletePending,
            onCancel: handleDeleteCancel,
            onConfirm: handleDeleteConfirm,
        }),
        [groupToDelete, isDeletePending, handleDeleteCancel, handleDeleteConfirm],
    );

    return useMemo(
        () => ({
            controller,
            handleBeforeDelete,
            requestDelete,
            deleteConfirmationModalProps,
        }),
        [controller, handleBeforeDelete, requestDelete, deleteConfirmationModalProps],
    );
};
